import { useEffect, useState } from "react";
import {
  AuthUser,
  confirmSignIn,
  getCurrentUser,
  resetPassword,
  signIn,
  SignInInput,
  SignInOutput,
  signOut,
} from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import { tryCatch } from "../../../_app/utils";
import UILoader from "../../../_app/components/UILoader";
import { AjaxResponse } from "../../../_app/api";
import { migrateSsoUser } from "../../api";
import { useCognitoUserpoolPolicy } from "../../hooks";
import { ChangePasswordForm } from "../../types";
import { buildResetPasswordInput } from "../../utils";
import SignInRedirect from "../SignInRedirect";
import SignInForm, { SignInFormValues } from "./SignInForm";
import ConfirmTotpForm from "./ConfirmTotpForm";
import ForgotPasswordForm from "./ForgotPasswordForm";
import CognitoPasswordChangeForm from "./CognitoPasswordChangeForm";

type Step = "signIn" | "confirmTotp" | "forceNewPassword" | "forgotPassword";

// Cognito stores email-based usernames lower-cased, so normalize before any auth call to
// avoid "user not found" mismatches when the user types a stray capital.
const normalizeEmail = (email: string) => email.trim().toLowerCase();

// Known Cognito failures get our own copy (also avoids user-enumeration wording differences);
// anything else falls back to the thrown message, which covers our own deliberate errors
// (e.g. the SSO-migration password-policy one).
const getSignInErrorMessage = (e: unknown): string => {
  if (!(e instanceof Error)) return "Incorrect email or password.";
  switch (e.name) {
    case "NotAuthorizedException":
    case "UserNotFoundException":
      return "Incorrect email or password.";
    default:
      return e.message || "Incorrect email or password.";
  }
};

interface CognitoSignInProps {
  // Opens the legacy/SSO "your password must be reset" dialog owned by the Login screen.
  onRequirePasswordReset: (username: string) => void;
}

const CognitoSignIn = ({ onRequirePasswordReset }: CognitoSignInProps) => {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("signIn");
  // Only the force-new-password step needs the policy, so don't fetch it on every login view.
  const { data: passwordPolicy, isFetching: isPolicyFetching } = useCognitoUserpoolPolicy({
    enabled: step === "forceNewPassword",
  });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isSessionChecked, setIsSessionChecked] = useState(false);

  // The old Authenticator redirected an already-signed-in user (back button, second tab) straight
  // away; without this check they would get the form and signIn would throw
  // UserAlreadyAuthenticatedException.
  useEffect(() => {
    let cancelled = false;
    tryCatch(getCurrentUser()).then(({ data }) => {
      if (cancelled) return;
      if (data) setUser(data);
      setIsSessionChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const goToStep = (next: Step) => {
    setError(null);
    setStep(next);
  };

  const completeSignIn = async () => {
    const authUser = await getCurrentUser();
    setUser(authUser);
  };

  // Mirrors the original Authenticator `services.handleSignIn`: attempts sign in, and on a
  // NotAuthorizedException tries to migrate a legacy SSO user before retrying.
  const performSignIn = async (inputs: SignInInput): Promise<SignInOutput> => {
    const { data: success, error: signInError } = await tryCatch(signIn(inputs));

    if (success) return success;

    if (signInError.name === "NotAuthorizedException") {
      const { error: migrationError } = await tryCatch(migrateSsoUser(inputs.username, inputs.password));

      // Migration succeeded — retry outside any try/catch so a retry failure surfaces as-is.
      if (!migrationError) return signIn(inputs);

      const errorResponse = migrationError as unknown as AjaxResponse;
      if (errorResponse?.data?.message === "Password does not meet Cognito requirements.") {
        onRequirePasswordReset(inputs.username);
        throw new Error("Your password no longer meets our updated security policy and must be changed.");
      }
    }

    throw signInError;
  };

  // Returns whether sign-in advanced (completed or moved to a known challenge). `false` means the
  // step could not be handled and an error was surfaced — callers should treat it as a failure.
  const handleNextStep = async (output: SignInOutput): Promise<boolean> => {
    if (output.isSignedIn) {
      await completeSignIn();
      return true;
    }

    switch (output.nextStep.signInStep) {
      case "DONE":
        await completeSignIn();
        return true;
      case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
        goToStep("confirmTotp");
        return true;
      case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
        goToStep("forceNewPassword");
        return true;
      case "RESET_PASSWORD":
        setError('You need to reset your password. Use "Forgot your password?" below to receive a reset link.');
        return false;
      default:
        console.error(`Unhandled sign-in next step: ${output.nextStep.signInStep}`);
        setError("We couldn't complete sign in. Please try again, or contact support if the issue continues.");
        return false;
    }
  };

  const handleSignIn = async ({ email, password }: SignInFormValues) => {
    setError(null);
    setIsLoading(true);
    try {
      const output = await performSignIn({ username: normalizeEmail(email), password });
      await handleNextStep(output);
    } catch (e) {
      // A session appeared since the mount check (e.g. signed in from another tab) — just proceed.
      if (e instanceof Error && e.name === "UserAlreadyAuthenticatedException") {
        await completeSignIn();
        return;
      }
      setError(getSignInErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmTotp = async (code: string): Promise<boolean> => {
    setError(null);
    setIsLoading(true);
    try {
      const output = await confirmSignIn({ challengeResponse: code });
      return await handleNextStep(output);
    } catch (e) {
      if (e instanceof Error && e.name === "CodeMismatchException") {
        setError("That code wasn't correct. Please try again.");
      } else {
        // The challenge session is no longer usable (too many wrong codes, or it timed out) —
        // no further code can succeed, so restart the sign-in instead of a retry dead-end.
        setStep("signIn");
        setError("Your sign-in session has expired. Please sign in again.");
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Abandon an in-progress MFA challenge and return to the sign-in form.
  const handleTotpBack = async () => {
    await tryCatch(signOut());
    goToStep("signIn");
  };

  const handleForceNewPassword = async (form: ChangePasswordForm) => {
    setError(null);
    setIsLoading(true);
    try {
      const output = await confirmSignIn({ challengeResponse: form.password });
      await handleNextStep(output);
    } catch {
      setError("Password change was not successful. Please try again or contact support if the issue persists.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (email: string) => {
    setError(null);
    setIsLoading(true);
    try {
      await signOut();
      await resetPassword(buildResetPasswordInput(normalizeEmail(email)));
      navigate("/auth/feedback", {
        state: {
          title: "Reset Password",
          type: "success",
          message:
            "We've sent you an email with a link to reset your password. Please check your inbox (and spam folder just in case) and follow the instructions to create a new password.",
        },
      });
    } catch {
      setError("We couldn't start the password reset. Please try again, or contact support if the issue continues.");
    } finally {
      setIsLoading(false);
    }
  };

  if (user) {
    return <SignInRedirect user={user} />;
  }

  // Don't flash the sign-in form (or accept a submit) until we know there is no existing session.
  if (!isSessionChecked) {
    return <UILoader />;
  }

  switch (step) {
    case "confirmTotp":
      return (
        <ConfirmTotpForm
          onSubmit={handleConfirmTotp}
          onBack={handleTotpBack}
          onDismissError={() => setError(null)}
          error={error}
          isLoading={isLoading}
        />
      );
    case "forceNewPassword":
      if (isPolicyFetching) return <UILoader />;
      return (
        <CognitoPasswordChangeForm
          heading="Change Password"
          passwordPolicy={passwordPolicy}
          onSubmit={handleForceNewPassword}
          error={error}
          onDismissError={() => setError(null)}
          isLoading={isLoading}
        />
      );
    case "forgotPassword":
      return (
        <ForgotPasswordForm
          onSubmit={handleForgotPassword}
          onBack={() => goToStep("signIn")}
          onDismissError={() => setError(null)}
          error={error}
          isLoading={isLoading}
        />
      );
    case "signIn":
    default:
      return (
        <SignInForm
          onSubmit={handleSignIn}
          onForgotPassword={() => goToStep("forgotPassword")}
          onDismissError={() => setError(null)}
          error={error}
          isLoading={isLoading}
        />
      );
  }
};

export default CognitoSignIn;
